import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../services/authService';

const AuthCallback = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const hasProcessed = useRef(false);

    useEffect(() => {
        const handleCallback = async () => {
            // Prevent duplicate execution in React StrictMode
            if (hasProcessed.current) {
                return;
            }
            hasProcessed.current = true;

            const code = searchParams.get('code');
            const errorParam = searchParams.get('error');

            if (errorParam) {
                setError('Authentication failed. Please try again.');
                setIsLoading(false);
                setTimeout(() => navigate('/login'), 3000);
                return;
            }

            if (!code) {
                setError('No authorization code received.');
                setIsLoading(false);
                setTimeout(() => navigate('/login'), 3000);
                return;
            }

            try {
                // Determine which provider based on current path
                const path = window.location.pathname;
                const provider = path.includes('google') ? 'google' : 'microsoft';

                // Call your backend API to exchange the code for a token
                const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

                const response = await fetch(`${apiBaseUrl}/auth/${provider}/callback?code=${code}`, {
                    method: 'GET',
                    credentials: 'include', // Enable cookies for cross-origin requests
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                console.log(`[AuthCallback] Response status: ${response.status}`);

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    console.error('[AuthCallback] Error from backend:', errorData);
                    throw new Error(errorData.error || 'Authentication failed');
                }

                const data = await response.json();
                // Check if authentication was successful
                if (!data.success) {
                    throw new Error(data.error || 'Authentication failed');
                }

                // Store the token and user data if provided (legacy support)
                if (data.token) {
                    localStorage.setItem('auth_token', data.token);
                }
                if (data.user) {
                    localStorage.setItem('user', JSON.stringify(data.user));
                }

                window.location.href = '/cms/reservations';
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Authentication failed. Please try again.');
                setIsLoading(false);
                setTimeout(() => navigate('/login'), 3000);
            }
        };

        handleCallback();
    }, [searchParams, navigate]);

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8f9fa'
        }}>
            <div style={{
                backgroundColor: '#ffffff',
                padding: '40px',
                borderRadius: '8px',
                boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
                textAlign: 'center',
                maxWidth: '400px'
            }}>
                {isLoading ? (
                    <>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #d89d37',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 20px'
                        }}></div>
                        <h3 style={{ color: '#2c3d4f', marginBottom: '10px' }}>
                            Authenticating...
                        </h3>
                        <p style={{ color: '#666' }}>
                            Please wait while we complete your login.
                        </p>
                        <style>{`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}</style>
                    </>
                ) : (
                    <>
                        <div style={{
                            fontSize: '48px',
                            marginBottom: '20px'
                        }}>
                            ❌
                        </div>
                        <h3 style={{ color: '#EA4335', marginBottom: '10px' }}>
                            Authentication Failed
                        </h3>
                        <p style={{ color: '#666' }}>
                            {error}
                        </p>
                        <p style={{ color: '#999', fontSize: '14px', marginTop: '20px' }}>
                            Redirecting to login page...
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default AuthCallback;